/*
  Variations tab pieces.
  Wave 1 (screen 11):
  - VariationsSummary — Approved total · Contract uplift · Proposed · Invoiced from VOs (idea 1)
  - VariationsTable   — read-first table: VO code, title + item preview, items, value, % of contract, status pill,
                        one "⋯" for edit / open in invoicing / delete; each VO is a card on phones (idea 2)
  Wave 2 (screen 11):
  - VariationsTable rows open (chevron or a click on the row) into VariationDetails: the items and quantities,
    the payment terms with what was invoiced against each, and the VO's invoices with a link to Invoicing (#3)
  - VariationPanel    — Add / Edit in a right drawer: items picked from the BOQ, pasted from Excel, typed as
                        free text or taken from Material shortages; value; payment terms; one Save (#4, #5)
  Wave 3 (screen 11):
  - the expanded row adds the approval trail (VariationTrail.jsx, #6): Proposed → Sent → Approved with dates,
    references and attachments, and a flag when a VO waits on the consultant for more than 14 days
  - the table gets an Invoicing column per VO (#7): received / submitted / not invoiced, into Invoicing or Raise
  - a VO raised on site (#8) shows where, who and its photos, and "Needs pricing" until it has a value
  BUG-006 (owner decision 2026-10-04): the VO's invoices in the expanded row show "Deletion requested · <name>" and a ⋯
  with the deletion items for this user (components/supply-finance/invoiceDeletion.js): Request deletion… (Invoicing
  editors), Delete invoice… (admins), Approve / Reject / Cancel on a requested invoice. The server decides.
  Owner rule 13 Oct 2026: VO money — value, "% of contract", approved / proposed totals, contract uplift, payment
  terms and their amounts — only for roles with Invoicing. `showFinance` (the page passes canAccessInvoicing) hides
  all of it, and so does a VO the server marked `finance_hidden` (it then has no value fields at all); the items,
  quantities, status and description stay for everyone who opens the tab, and the drawer neither shows nor sends
  the money fields (the server keeps the stored ones).
*/
import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  ArrowRight, ChevronDown, ChevronRight, ClipboardPaste, GitCompareArrows, ListPlus, PackageSearch, Pencil, Plus, ReceiptText, Search, Trash2, X,
} from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { RowActions } from '@/components/ui/row-actions';
import { StatCard } from '@/components/ui/stat-card';
import { StatusPill } from '@/components/ui/status-pill';
import { Segmented } from '@/components/ui/segmented';
import { SidePanel, SidePanelSection } from '@/components/ui/side-panel';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { formatDate, formatNumber, formatPercent } from '@/lib/format';
import { MONO, Panel } from './ProjectUi';
import { matchInvoicesToTerms } from '@/lib/projectPaymentSummary';
import { formatCodeList, parseMoneyInput, variationItemsPreview } from './projectDetailModel';
import { FollowUpFlag, SiteSourceNote, VariationInvoicingCell, VariationTrailFeed } from './VariationTrail';
import { variationFollowUp, variationInvoicing } from './variationTrailModel';
import { parseQuantity } from './materialExcel';
import { useIsPhone } from '@/hooks/useMediaQuery';
import { csvRow } from '@/components/documents-ai/csv';
import { canEdit as canEditModule } from '@/lib/permissions';
import { DeletionRequestPill, useInvoiceDeletion } from '@/components/supply-finance/invoiceDeletion';

const money = (value) => formatNumber(value, { decimals: 2 });
const qtyText = (value) => formatNumber(value, { maxDecimals: 2 });
const pct = (value) => `${formatNumber(value, { maxDecimals: 2 })}%`;
const variationValue = (variation) => Number(variation?.total_value ?? variation?.financial_impact ?? 0) || 0;
const sortByCode = (list) => [...list].sort((a, b) => String(a.vo_number || '').localeCompare(String(b.vo_number || ''), undefined, { numeric: true }));
const itemQty = (item) => Number(item?.qty ?? item?.qty_change ?? 0) || 0;
/** VO money is shown only with `showFinance` and while no VO came back without it (`finance_hidden`). */
const financeVisible = (showFinance, variations = []) => Boolean(showFinance) && !(variations || []).some((variation) => variation?.finance_hidden);

export function VoCode({ code, className }) {
  return (
    <span className={cn('inline-flex h-[22px] items-center whitespace-nowrap rounded-md bg-status-violet-bg px-1.5 text-[12px] font-semibold text-status-violet-ink', MONO, className)}>
      {code || 'VO'}
    </span>
  );
}

export function VariationsSummary({ summary, variations = [], consultant, onOpenInvoicing, showFinance = true }) {
  const approvedCodes = sortByCode(variations.filter((variation) => variation.status === 'Approved')).map((variation) => variation.vo_number).filter(Boolean);
  const waitingOn = consultant ? `waiting on ${consultant}` : 'waiting for approval';
  const overdue = variations.filter((variation) => variationFollowUp(variation)).length;
  const compactCards = useIsPhone();
  if (!financeVisible(showFinance, variations)) {
    // Without Invoicing: the counts only — no totals, no uplift, nothing invoiced.
    return (
      <div className="grid grid-cols-2 gap-3" data-testid="variations-summary">
        <StatCard
          label="Approved"
          size={compactCards ? 'compact' : 'default'}
          value={formatNumber(summary.approvedCount, { decimals: 0 })}
          meta={summary.approvedCount
            ? (approvedCodes.length ? formatCodeList(approvedCodes) : `${summary.approvedCount} VO${summary.approvedCount === 1 ? '' : 's'}`)
            : 'No approved variations yet'}
        />
        <StatCard
          label="Proposed"
          size={compactCards ? 'compact' : 'default'}
          value={formatNumber(summary.proposedCount, { decimals: 0 })}
          valueTone={summary.proposedCount ? 'amber' : undefined}
          meta={summary.proposedCount
            ? `${waitingOn}${overdue ? ` · ${overdue} over 14 d` : ''}`
            : `nothing ${waitingOn}`}
          metaTone={overdue ? 'amber' : undefined}
        />
      </div>
    );
  }
  return (
    // phones: two cards a row (compact) — the four used to stack ~560px above the first VO
    <div className="grid grid-cols-2 gap-3 xl:grid-cols-4" data-testid="variations-summary">
      <StatCard
        label="Approved"
        size={compactCards ? 'compact' : 'default'}
        value={money(summary.approvedTotal)}
        meta={summary.approvedCount
          ? `${summary.approvedCount} VO${summary.approvedCount === 1 ? '' : 's'}${approvedCodes.length ? ` · ${formatCodeList(approvedCodes)}` : ''}`
          : 'No approved variations yet'}
      />
      <StatCard
        label="Contract uplift"
        size={compactCards ? 'compact' : 'default'}
        value={summary.upliftPercent === null ? '—' : `+${formatPercent(summary.upliftPercent, { decimals: 1 })}`}
        meta={`${formatNumber(summary.baseValue, { decimals: 0 })} → ${formatNumber(summary.contractValue, { decimals: 0 })}`}
      />
      <StatCard
        label="Proposed"
        size={compactCards ? 'compact' : 'default'}
        value={formatNumber(summary.proposedCount, { decimals: 0 })}
        valueTone={summary.proposedCount ? 'amber' : undefined}
        meta={summary.proposedCount
          ? `${money(summary.proposedTotal)} ${waitingOn}${overdue ? ` · ${overdue} over 14 d` : ''}`
          : `nothing ${waitingOn}`}
        metaTone={overdue ? 'amber' : undefined}
      />
      <StatCard
        label={onOpenInvoicing ? (
          <span className="flex items-center justify-between gap-2">
            <span className="truncate">Invoiced from VOs</span>
            <button
              type="button"
              onClick={onOpenInvoicing}
              className="inline-flex shrink-0 items-center gap-0.5 text-[12px] font-semibold text-brand-ink hover:underline"
              data-testid="variations-open-invoicing"
            >
              Invoicing
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </span>
        ) : 'Invoiced from VOs'}
        size={compactCards ? 'compact' : 'default'}
        value={money(summary.invoiced)}
        progress={summary.invoicedPercent ?? 0}
        progressTone="lime"
        progressLabel="Invoiced share of approved variations"
        meta={summary.approvedTotal
          ? <span className="whitespace-normal">{formatPercent(summary.invoicedPercent ?? 0, { decimals: 0 })} of approved · {money(summary.received)} received</span>
          : 'No approved variations to invoice'}
      />
    </div>
  );
}

/**
 * Export (11): the VO rows as CSV — VO, description, items, value, % of contract, status, invoiced, received.
 * Without `showFinance` (no Invoicing) only VO, description, items and status: no money column at all.
 */
export function variationsToCsv(variations = [], variationInvoices = [], baseValue = 0, { showFinance = true } = {}) {
  if (!financeVisible(showFinance, variations)) {
    return [
      csvRow(['VO', 'Description', 'Items', 'Status']),
      ...sortByCode(variations).map((variation) => csvRow([
        variation.vo_number || '',
        variation.description || '',
        variationItemsPreview(variation.items).count,
        variation.status || 'Proposed',
      ])),
    ].join('\n');
  }
  const header = ['VO', 'Description', 'Items', 'Value', '% of contract', 'Status', 'Invoiced', 'Received'];
  const lines = sortByCode(variations).map((variation) => {
    const value = variationValue(variation);
    const state = variationInvoicing(variation, variationInvoices);
    const received = variationInvoices
      .filter((invoice) => invoice.variation_id === variation.id && invoice.status === 'Payment Received')
      .reduce((sum, invoice) => sum + (Number(invoice.amount) || 0), 0);
    return csvRow([
      variation.vo_number || '',
      variation.description || '',
      variationItemsPreview(variation.items).count,
      value.toFixed(2),
      baseValue > 0 ? ((value / baseValue) * 100).toFixed(2) : '',
      variation.status || 'Proposed',
      Number(state.invoiced || 0).toFixed(2),
      received.toFixed(2),
    ]);
  });
  return [csvRow(header), ...lines].join('\n');
}

/* ------------------------------------------------------------------ expanded row (#3) */

const UpperLabel = ({ children }) => (
  <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">{children}</p>
);

function termAmount(term, value) {
  const target = Number(term?.target_amount || 0);
  if (target > 0) return target;
  const percent = Number(term?.percentage || 0);
  return percent > 0 ? (value * percent) / 100 : 0;
}

/** Items · payment terms (with what was invoiced against each) · the VO's invoices. Without `showFinance` only the items. */
export function VariationDetails({
  variation, invoices = [], showInvoices = true, showFinance = true, onOpenInvoicing, onEdit, canEdit, projectId, consultant, canRecordTrail, onTrailChanged,
  invoiceMenu,
}) {
  const finance = financeVisible(showFinance, [variation]);
  const items = (variation.items || []).filter((item) => item && typeof item === 'object');
  const value = variationValue(variation);
  const terms = useMemo(() => (Array.isArray(variation.payment_terms) ? variation.payment_terms : []), [variation.payment_terms]);
  const totalQty = items.reduce((sum, item) => sum + itemQty(item), 0);
  // Per-term invoice state needs the invoices: roles without Invoicing see the terms without it.
  const termInvoices = useMemo(() => matchInvoicesToTerms(terms, showInvoices ? invoices : []), [terms, invoices, showInvoices]);
  return (
    <div className="space-y-3 px-5 py-4" data-testid={`variation-details-${variation.id}`}>
    <SiteSourceNote variation={variation} projectId={projectId} showFinance={finance} />
    <div className={cn('grid gap-6', finance ? 'lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)_minmax(0,1fr)]' : 'lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]')}>
      <section aria-label={`Items of ${variation.vo_number || 'variation'}`}>
        <UpperLabel>Items{items.length ? ` · ${qtyText(totalQty)} pcs` : ''}</UpperLabel>
        {items.length ? (
          <ul className="divide-y divide-dashed divide-line">
            {items.map((item, index) => (
              <li key={`${item.part_number || 'item'}-${index}`} className="grid grid-cols-[minmax(0,auto)_minmax(0,1fr)_auto] items-baseline gap-3 py-1.5 text-[13px]">
                <span className={cn(MONO, 'truncate text-[12.5px] font-semibold text-ink')}>{item.part_number && item.part_number !== '-' ? item.part_number : '—'}</span>
                <span className="truncate text-ink-2" title={item.description || ''}>{item.description || 'No description'}</span>
                <span className="whitespace-nowrap tabular-nums text-ink-2">
                  ×{qtyText(itemQty(item))}{item.unit && item.unit !== 'pcs' ? ` ${item.unit}` : ''}
                  {itemQty(item) < 0 ? <span className="ml-1.5 text-[11.5px] font-semibold text-status-red-ink">omitted</span> : null}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[13px] text-ink-3">No items — this VO only changes the contract value.</p>
        )}
      </section>

      {finance ? (
      <div className="min-w-0">
      <section aria-label={`Payment terms of ${variation.vo_number || 'variation'}`}>
        <UpperLabel>Payment terms</UpperLabel>
        {terms.length ? (
          <>
            <ul className="space-y-2">
              {termInvoices.byTerm.map(({ term, invoices: linked, invoiced, received, status }, index) => {
                const amount = termAmount(term, value);
                return (
                  <li key={term.id || `${term.name}-${index}`} className="rounded-lg border border-line bg-surface px-3 py-2" data-testid="variation-term">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-[13px] font-semibold text-ink">
                          {term.percentage ? `${pct(term.percentage)} ` : ''}{term.name}
                        </p>
                        <p className="text-[12px] text-ink-3">
                          {amount ? money(amount) : 'No amount set'}
                          {showInvoices && invoiced > 0
                            ? ` · invoiced ${money(invoiced)}${received > 0 && received < invoiced ? ` · received ${money(received)}` : ''}`
                            : ''}
                        </p>
                      </div>
                      {!showInvoices ? null : status ? (
                        <StatusPill
                          status={status}
                          domain="invoice"
                          size="sm"
                          title={linked.length > 1 ? `${linked.length} invoices: ${linked.map((invoice) => invoice.reference || invoice.invoice_type || 'invoice').join(', ')}` : undefined}
                        />
                      ) : termInvoices.unlinked.length ? (
                        <StatusPill tone="amber" label="No linked invoice" size="sm" title="This VO has invoices that are not linked to a payment term — see the note below" />
                      ) : (
                        <StatusPill tone="gray" label="Not invoiced" size="sm" />
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            {showInvoices && termInvoices.unlinked.length ? (
              <p className="mt-2 text-[12px] leading-snug text-ink-3" data-testid="variation-unlinked-invoices">
                {termInvoices.unlinked.length === 1 ? '1 invoice is' : `${termInvoices.unlinked.length} invoices are`} not linked to a payment term:{' '}
                {termInvoices.unlinked.map((invoice) => `${invoice.invoice_type || 'Invoice'} ${money(invoice.amount)}`).join(', ')}.
              </p>
            ) : null}
          </>
        ) : (
          <p className="text-[13px] text-ink-3">
            No payment terms yet.
            {canEdit && onEdit ? (
              <button type="button" className="ml-1 font-semibold text-brand-ink hover:underline" onClick={() => onEdit(variation)}>Add terms</button>
            ) : null}
          </p>
        )}
      </section>

      {showInvoices ? (
        <section aria-label={`Invoices of ${variation.vo_number || 'variation'}`} className="mt-4">
          <UpperLabel>Invoices</UpperLabel>
          {invoices.length ? (
            <ul className="space-y-1.5">
              {invoices.map((invoice) => {
                const menu = invoiceMenu ? invoiceMenu(invoice).filter(Boolean) : [];
                return (
                  <li key={invoice.id} className="flex flex-col gap-1 text-[13px]" data-testid={`variation-invoice-${invoice.id}`}>
                    <span className="flex items-center justify-between gap-3">
                      <span className="min-w-0 truncate" title={`${invoice.reference || ''} ${invoice.invoice_type || 'Invoice'}`.trim()}>
                        <span className={cn(MONO, 'mr-2 font-semibold text-ink')}>{invoice.reference || '—'}</span>
                        <span className="text-ink-3">{invoice.invoice_type || 'Invoice'}</span>
                      </span>
                      <span className="flex shrink-0 items-center gap-2">
                        <span className="tabular-nums text-ink-2">{money(invoice.amount)}</span>
                        <StatusPill status={invoice.status || 'Not Started'} domain="invoice" size="sm" />
                        {menu.length ? (
                          <RowActions
                            size="sm"
                            label={`Actions for VO invoice ${invoice.reference || invoice.invoice_type || ''}`.trim()}
                            items={menu}
                            contentClassName="min-w-[240px]"
                            data-testid={`variation-invoice-actions-${invoice.id}`}
                          />
                        ) : null}
                      </span>
                    </span>
                    {invoice.deletion_request ? (
                      <span className="flex min-w-0">
                        <DeletionRequestPill invoice={invoice} className="max-w-full truncate" testId={`variation-invoice-deletion-pill-${invoice.id}`} />
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-[13px] text-ink-3">No invoices raised for this VO yet.</p>
          )}
          {onOpenInvoicing ? (
            <button
              type="button"
              onClick={() => onOpenInvoicing(variation)}
              className="mt-3 inline-flex items-center gap-1 text-[13px] font-semibold text-brand-ink hover:underline"
              data-testid={`variation-open-invoicing-${variation.id}`}
            >
              Open {variation.vo_number || 'this VO'} in Invoicing
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          ) : null}
        </section>
      ) : null}
      </div>
      ) : null}

      <VariationTrailFeed
        variation={variation}
        projectId={projectId}
        consultant={consultant}
        canEdit={canRecordTrail}
        onChanged={onTrailChanged}
      />
    </div>
    </div>
  );
}

/* ------------------------------------------------------------------ table */

export function VariationsTable({
  variations = [],
  variationInvoices: variationInvoicesProp = [],
  baseValue = 0,
  canEdit,
  showInvoices = true,
  showFinance = true,
  expandedIds = [],
  onToggle,
  onEdit,
  onDelete,
  onOpenInvoicing,
  onRaiseInvoice,
  emptyAction,
  projectId,
  consultant,
  canRecordTrail = false,
  onTrailChanged,
  onInvoicesChanged,
}) {
  // BUG-006: deleting a VO invoice needs an Admin's approval (a request / approval shows here at once)
  const deletion = useInvoiceDeletion({ onChanged: onInvoicesChanged });
  const variationInvoices = variationInvoicesProp.map(deletion.apply).filter(Boolean);
  const canEditInvoices = showInvoices && canEditModule(deletion.user, 'invoicing');
  const invoiceMenu = canEditInvoices
    ? (invoice) => deletion.menuItems(invoice, { canEdit: true, kind: 'vo', testIdPrefix: 'variation-invoice' })
    : null;
  const rows = sortByCode(variations);
  const finance = financeVisible(showFinance, rows);
  const approved = rows.filter((variation) => variation.status === 'Approved');
  const approvedTotal = approved.reduce((sum, variation) => sum + variationValue(variation), 0);
  const share = (value) => (baseValue > 0 ? formatPercent((value / baseValue) * 100, { decimals: 2 }) : '—');
  const expanded = new Set(expandedIds);
  const invoicesOf = (variation) => variationInvoices.filter((invoice) => invoice.variation_id === variation.id);
  const columnCount = (showInvoices ? 9 : 8) - (finance ? 0 : 2);
  const detailsFor = (variation) => (
    <VariationDetails
      variation={variation}
      invoices={invoicesOf(variation)}
      showInvoices={showInvoices}
      showFinance={finance}
      onOpenInvoicing={onOpenInvoicing}
      onEdit={onEdit}
      canEdit={canEdit}
      projectId={projectId}
      consultant={consultant}
      canRecordTrail={canRecordTrail}
      onTrailChanged={onTrailChanged}
      invoiceMenu={invoiceMenu}
    />
  );

  // BUG-006: a VO that still has invoices takes them with it, so only an Admin deletes it (the server answers 409
  // to anyone else); others ask for each invoice's deletion first.
  const deleteBlocked = (variation) => !deletion.isApprover && invoicesOf(variation).length > 0;
  const menuFor = (variation) => [
    canEdit && { label: 'Edit variation', icon: Pencil, onSelect: () => onEdit(variation), testId: `variation-edit-${variation.id}` },
    onOpenInvoicing && { label: 'Open in Invoicing', icon: ReceiptText, onSelect: () => onOpenInvoicing(variation) },
    canEdit && (deleteBlocked(variation)
      ? { label: 'Delete variation…', icon: Trash2, disabled: true, hint: 'Has invoices', testId: `variation-delete-${variation.id}` }
      : { label: 'Delete variation…', icon: Trash2, danger: true, onSelect: () => onDelete(variation), testId: `variation-delete-${variation.id}` }),
  ];

  if (!rows.length) {
    return (
      <Panel className="px-5 py-8 text-center" data-testid="variations-table">
        <p className="text-[14px] font-semibold text-ink">No variations yet</p>
        <p className="mt-1 text-[13px] text-ink-3">Change orders on top of the base contract show up here with their {finance ? 'value' : 'items'} and approval status.</p>
        {emptyAction ? <div className="mt-4 flex justify-center">{emptyAction}</div> : null}
      </Panel>
    );
  }

  const toggleFromRow = (event, variation) => {
    if (event.target.closest('button, a, input, [role="menuitem"]') && !event.target.closest('[data-variation-chevron]')) return;
    if (!event.currentTarget.contains(event.target)) return; // clicks inside a portal (menu) bubble here too
    onToggle(variation.id);
  };

  return (
    <Panel className="overflow-hidden" data-testid="variations-table">
      {/* Desktop / tablet: table */}
      <div className="relative hidden overflow-x-auto md:block">
        <table className="w-full border-separate border-spacing-0 text-[13px] text-ink">
          <thead>
            <tr className="text-left text-[11.5px] font-bold text-ink-3">
              <th className="h-10 w-10 border-b border-line bg-surface-2 pl-3"><span className="sr-only">Open</span></th>
              <th className="h-10 border-b border-line bg-surface-2 px-3">VO</th>
              <th className="h-10 border-b border-line bg-surface-2 px-3">Description</th>
              <th className="hidden h-10 border-b border-line bg-surface-2 px-3 text-right xl:table-cell">Items</th>
              {finance ? (
                <>
                  <th className="h-10 border-b border-line bg-surface-2 px-3 text-right">Value</th>
                  <th className="h-10 border-b border-line bg-surface-2 px-3 text-right whitespace-nowrap">% of contract</th>
                </>
              ) : null}
              <th className="h-10 border-b border-line bg-surface-2 px-3">Status</th>
              {showInvoices ? <th className="h-10 border-b border-line bg-surface-2 px-3">Invoicing</th> : null}
              <th className="h-10 w-12 border-b border-line bg-surface-2 px-3"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((variation) => {
              const preview = variationItemsPreview(variation.items);
              const value = variationValue(variation);
              const open = expanded.has(variation.id);
              return (
                <Fragment key={variation.id}>
                  <tr
                    className={cn('cursor-pointer hover:bg-surface-hover', open && 'bg-surface-2')}
                    data-testid={`variation-row-${variation.id}`}
                    onClick={(event) => toggleFromRow(event, variation)}
                  >
                    <td className={cn('border-b border-line-2 py-2.5 pl-3 align-top', open && 'border-transparent')}>
                      <button
                        type="button"
                        data-variation-chevron
                        aria-expanded={open}
                        aria-label={`${open ? 'Close' : 'Open'} ${variation.vo_number || 'variation'} details`}
                        onClick={(event) => { event.stopPropagation(); onToggle(variation.id); }}
                        className="grid h-6 w-6 place-items-center rounded-md text-ink-3 hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {open ? <ChevronDown className="h-4 w-4" aria-hidden="true" /> : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
                      </button>
                    </td>
                    <td className={cn('border-b border-line-2 px-3 py-2.5 align-top', open && 'border-transparent')}><VoCode code={variation.vo_number} /></td>
                    <td className={cn('w-full min-w-[220px] max-w-0 border-b border-line-2 px-3 py-2.5 align-top', open && 'border-transparent')}>
                      <div className="truncate text-[13.5px] font-semibold text-ink" title={variation.description || ''}>{variation.description || 'No description'}</div>
                      {preview.text ? <div className={cn('mt-0.5 truncate text-[12px] text-ink-3')} title={preview.text}>{preview.text}</div> : null}
                      {variation.source?.kind === 'site' ? (
                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                          <StatusPill tone="violet" label="From site" size="sm" />
                          {finance && variation.needs_pricing && !(value > 0) ? <StatusPill tone="amber" label="Needs pricing" size="sm" /> : null}
                        </div>
                      ) : null}
                    </td>
                    <td className={cn('hidden border-b border-line-2 px-3 py-2.5 text-right align-top tabular-nums text-ink-2 xl:table-cell', open && 'border-transparent')}>{preview.count || '—'}</td>
                    {finance ? (
                      <>
                        <td className={cn('border-b border-line-2 px-3 py-2.5 text-right align-top font-semibold tabular-nums', open && 'border-transparent')}>{money(value)}</td>
                        <td className={cn('border-b border-line-2 px-3 py-2.5 text-right align-top tabular-nums text-ink-2', open && 'border-transparent')}>{share(value)}</td>
                      </>
                    ) : null}
                    <td className={cn('border-b border-line-2 px-3 py-2.5 align-top', open && 'border-transparent')}>
                      <div className="flex flex-col items-start gap-1">
                        <StatusPill status={variation.status || 'Proposed'} domain="variation" />
                        <FollowUpFlag followUp={variationFollowUp(variation)} consultant={consultant} />
                      </div>
                    </td>
                    {showInvoices ? (
                      <td className={cn('border-b border-line-2 px-3 py-2.5 align-top', open && 'border-transparent')}>
                        <VariationInvoicingCell
                          variation={variation}
                          state={variationInvoicing(variation, variationInvoices)}
                          onOpen={onOpenInvoicing}
                          onRaise={canEdit ? onRaiseInvoice : null}
                        />
                      </td>
                    ) : null}
                    <td className={cn('border-b border-line-2 px-2 py-2 text-right align-top', open && 'border-transparent')}>
                      <RowActions label={`Actions for ${variation.vo_number || 'variation'}`} items={menuFor(variation)} data-testid={`variation-actions-${variation.id}`} />
                    </td>
                  </tr>
                  {open ? (
                    <tr data-testid={`variation-expanded-${variation.id}`}>
                      <td colSpan={columnCount} className="border-b border-line-2 bg-surface-2 p-0">
                        {detailsFor(variation)}
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="text-[13px]">
              <td colSpan={3} className="bg-surface-2 px-4 py-3 font-semibold text-ink-2">
                Total approved · {approved.length} VO{approved.length === 1 ? '' : 's'}
              </td>
              <td className="hidden bg-surface-2 px-3 py-3 text-right font-semibold tabular-nums text-ink-2 xl:table-cell" data-testid="variations-items-total">
                {formatNumber(approved.reduce((sum, variation) => sum + variationItemsPreview(variation.items).count, 0), { decimals: 0 })}
              </td>
              {finance ? (
                <>
                  <td className="bg-surface-2 px-3 py-3 text-right font-bold tabular-nums">{money(approvedTotal)}</td>
                  <td className="bg-surface-2 px-3 py-3 text-right font-bold tabular-nums">{share(approvedTotal)}</td>
                </>
              ) : null}
              <td className="bg-surface-2 px-3 py-3" />
              {showInvoices ? (
                <td className="bg-surface-2 px-3 py-3 text-[12.5px] font-semibold tabular-nums text-ink-2" data-testid="variations-invoiced-total">
                  {money(rows.reduce((sum, variation) => sum + variationInvoicing(variation, variationInvoices).invoiced, 0))} invoiced
                </td>
              ) : null}
              <td className="bg-surface-2 px-3 py-3" />
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Phones: one card per VO; a tap opens its details */}
      <ul className="divide-y divide-line md:hidden">
        {rows.map((variation) => {
          const preview = variationItemsPreview(variation.items);
          const value = variationValue(variation);
          const open = expanded.has(variation.id);
          return (
            <li key={variation.id} data-testid={`variation-card-${variation.id}`}>
              <div className="space-y-2 px-4 py-3">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    aria-expanded={open}
                    aria-label={`${open ? 'Close' : 'Open'} ${variation.vo_number || 'variation'} details`}
                    onClick={() => onToggle(variation.id)}
                    className="-ml-1 grid h-7 w-7 place-items-center rounded-md text-ink-3"
                  >
                    {open ? <ChevronDown className="h-4 w-4" aria-hidden="true" /> : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
                  </button>
                  <VoCode code={variation.vo_number} />
                  <StatusPill status={variation.status || 'Proposed'} domain="variation" size="sm" />
                  {finance ? <span className="ml-auto text-[14px] font-bold tabular-nums">{money(value)}</span> : <span className="ml-auto" />}
                  <RowActions label={`Actions for ${variation.vo_number || 'variation'}`} items={menuFor(variation)} />
                </div>
                <div className="text-[13.5px] font-semibold text-ink">{variation.description || 'No description'}</div>
                <div className="flex flex-wrap gap-x-3 text-[12px] text-ink-3">
                  {preview.text ? <span>{preview.text}</span> : null}
                  {finance ? <span>{share(value)} of contract</span> : null}
                </div>
                <FollowUpFlag followUp={variationFollowUp(variation)} consultant={consultant} />
                {showInvoices ? (
                  <VariationInvoicingCell variation={variation} state={variationInvoicing(variation, variationInvoices)} onOpen={onOpenInvoicing} onRaise={canEdit ? onRaiseInvoice : null} />
                ) : null}
              </div>
              {open ? (
                <div className="border-t border-line-2 bg-surface-2">
                  {detailsFor(variation)}
                </div>
              ) : null}
            </li>
          );
        })}
        <li className="flex items-center justify-between bg-surface-2 px-4 py-3 text-[13px]">
          <span className="font-semibold text-ink-2">Total approved · {approved.length} VO{approved.length === 1 ? '' : 's'}</span>
          {finance ? <span className="font-bold tabular-nums">{money(approvedTotal)} · {share(approvedTotal)}</span> : null}
        </li>
      </ul>
    </Panel>
  );
}

/* ------------------------------------------------------------------ drawer (#4, #5) */

let itemSequence = 0;
const nextKey = () => {
  itemSequence += 1;
  return `vo-item-${Date.now().toString(36)}-${itemSequence}`;
};
const newTermId = () => `term-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

/** A stored amount as the person reads it ("6,510.00"); parseMoneyInput reads it back. */
const moneyDraft = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && String(value ?? '').trim() !== '' ? formatNumber(number, { decimals: 2 }) : '';
};

const toDraftItem = (item = {}) => ({
  key: nextKey(),
  boq_item_id: item.boq_item_id || '',
  part_number: item.part_number || '',
  description: item.description || '',
  unit: item.unit || 'pcs',
  qty: String(itemQty(item) || item.qty || ''),
});

export function variationToDraft(variation) {
  if (!variation) {
    return { vo_number: '', description: '', status: 'Proposed', total_value: '', items: [], payment_terms: [] };
  }
  return {
    vo_number: variation.vo_number || '',
    description: variation.description || '',
    status: variation.status || 'Proposed',
    total_value: moneyDraft(variation.total_value ?? variation.financial_impact),
    items: (variation.items || []).filter((item) => item && typeof item === 'object').map(toDraftItem),
    payment_terms: (variation.payment_terms || []).map((term) => ({
      id: term.id || newTermId(),
      name: term.name || '',
      percentage: term.percentage ? String(term.percentage) : '',
      target_amount: term.target_amount ? moneyDraft(term.target_amount) : '',
    })),
  };
}

/** "VO-005" after VO-001…VO-004 (keeps the existing numbering style). */
export function suggestNextVoNumber(variations = []) {
  let best = 0;
  let width = 3;
  let prefix = 'VO-';
  (variations || []).forEach((variation) => {
    const match = String(variation.vo_number || '').match(/^(.*?)(\d+)\s*$/);
    if (!match) return;
    const number = Number(match[2]);
    if (number >= best) {
      best = number;
      width = match[2].length;
      prefix = match[1] || 'VO-';
    }
  });
  return `${prefix}${String(best + 1).padStart(width, '0')}`;
}

/** Paste from Excel: "Part No ⇥ Description ⇥ Qty" per line (a header line is skipped). */
export function parseVariationPaste(raw, boqItems = []) {
  const byPart = new Map(boqItems.map((item) => [String(item.part_number || '').trim().toUpperCase(), item]));
  const rows = String(raw || '').split(/\r?\n/).filter((line) => line.trim());
  const items = [];
  let skipped = 0;
  rows.forEach((line, index) => {
    const cells = line.includes('\t') ? line.split('\t') : line.trim().split(/\s{2,}|;|,(?=\s*\S)/);
    const [part = '', description = '', qtyCell = ''] = cells.map((cell) => String(cell || '').trim());
    const qty = parseQuantity(qtyCell);
    if (index === 0 && /part|model/i.test(part) && /desc/i.test(description)) return;
    if (!(qty > 0) || !(part || description)) {
      skipped += 1;
      return;
    }
    const match = part ? byPart.get(part.toUpperCase()) : null;
    items.push({
      boq_item_id: match?.id || '',
      part_number: match?.part_number || part,
      description: description || match?.description || '',
      unit: match?.unit || 'pcs',
      qty,
    });
  });
  return { items, skipped, matched: items.filter((item) => item.boq_item_id).length };
}

/**
 * Validates a draft → { payload, error }. Items with qty 0 are dropped; free-text items are allowed.
 * `includeFinance: false` (a role without Invoicing): no total_value and no payment_terms in the payload — the
 * person never saw them, and sending 0 / [] would wipe what Finance stored.
 */
export function buildVariationPayload(draft, { includeFinance = true } = {}) {
  const voNumber = String(draft.vo_number || '').trim();
  if (!voNumber) return { error: 'Give the variation a number, e.g. VO-005' };
  const total = !includeFinance || String(draft.total_value ?? '').trim() === '' ? { value: 0, error: '' } : parseMoneyInput(draft.total_value);
  if (total.error) return { error: `Total value: ${total.error}` };
  // A negative total / quantity is an omission (a deductive VO takes work out of the contract): allowed, as
  // on the server. Only a quantity of 0 is refused — the line would change nothing.
  const items = [];
  for (const item of draft.items || []) {
    const hasText = String(item.part_number || '').trim() || String(item.description || '').trim();
    const qty = parseQuantity(item.qty);
    if (!hasText && !qty) continue;
    if (!hasText) return { error: 'Each item needs a part number or a description' };
    if (!qty) return { error: `Enter a quantity for ${String(item.part_number || item.description).trim()} (negative for an omission)` };
    items.push({
      ...(item.boq_item_id ? { boq_item_id: item.boq_item_id } : {}),
      part_number: String(item.part_number || '').trim(),
      description: String(item.description || '').trim(),
      unit: String(item.unit || '').trim() || 'pcs',
      qty,
    });
  }
  const paymentTerms = [];
  for (const term of (includeFinance ? draft.payment_terms : null) || []) {
    const name = String(term.name || '').trim();
    const percentage = String(term.percentage ?? '').trim() === '' ? 0 : Number(term.percentage);
    const target = String(term.target_amount ?? '').trim() === '' ? { value: 0, error: '' } : parseMoneyInput(term.target_amount);
    if (!name && !percentage && !target.value) continue;
    if (!name) return { error: 'Each payment term needs a name, e.g. “On approval”' };
    if (!Number.isFinite(percentage) || percentage < 0 || percentage > 100) return { error: `${name}: the percentage must be between 0 and 100` };
    if (target.error) return { error: `${name}: ${target.error}` };
    if (target.value < 0) return { error: `${name}: the amount cannot be negative` };
    paymentTerms.push({ id: term.id, name, percentage, target_amount: target.value || 0 });
  }
  return {
    payload: {
      vo_number: voNumber,
      description: String(draft.description || '').trim(),
      ...(includeFinance ? { total_value: total.value || 0 } : {}),
      items,
      ...(includeFinance ? { payment_terms: paymentTerms } : {}),
      ...(draft.status ? { status: draft.status } : {}),
    },
    error: '',
  };
}

function BoqPicker({ boqItems = [], onPick }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const shown = (needle
    ? boqItems.filter((item) => `${item.part_number || ''} ${item.description || ''}`.toLowerCase().includes(needle))
    : boqItems).slice(0, 60);
  return (
    <Popover open={open} onOpenChange={(next) => { setOpen(next); if (!next) setQuery(''); }}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" disabled={!boqItems.length} title={boqItems.length ? undefined : 'This project has no BOQ lines yet'} data-testid="variation-add-from-boq">
          <ListPlus aria-hidden="true" />
          From BOQ
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[380px] rounded-xl border-line bg-surface p-2 text-ink shadow-overlay" onKeyDown={(event) => event.stopPropagation()}>
        <label className="flex h-9 items-center gap-2 rounded-lg border border-line px-2.5 text-ink-4">
          <Search className="h-4 w-4" aria-hidden="true" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Find a BOQ line…"
            aria-label="Find a BOQ line"
            className="min-w-0 flex-1 bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-4"
            autoFocus
          />
        </label>
        <ul className="mt-1.5 max-h-[280px] overflow-y-auto">
          {shown.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => onPick(item)}
                className="flex w-full items-baseline gap-3 rounded-lg px-2.5 py-1.5 text-left text-[13px] hover:bg-surface-hover"
              >
                <span className={cn(MONO, 'shrink-0 text-[12px] font-semibold text-ink')}>{item.part_number || '—'}</span>
                <span className="min-w-0 flex-1 truncate text-ink-2">{item.description || ''}</span>
                <Plus className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden="true" />
              </button>
            </li>
          ))}
          {!shown.length ? <li className="px-2.5 py-2 text-[12.5px] text-ink-3">No BOQ line matches “{query}”.</li> : null}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

const FIELD_LABEL = 'text-[12.5px] font-semibold text-ink-2';
const fieldClass = 'h-9 rounded-lg bg-surface text-[13.5px]';

/**
 * Add / Edit a variation in a right drawer. onSave(payload) is async and throws a readable Error on
 * failure (the panel stays open and shows it). `shortages` = Material rows that need more than BOQ + VOs.
 * `prefill` ({ items, description }) — a new variation drafted elsewhere (05 #8 "Draft VO-005" from the
 * Material tab's "Variation needed" callout): the lines start filled in, and the draft counts as unsaved.
 */
export function VariationPanel({ open, onOpenChange, variation = null, variations = [], boqItems = [], shortages = [], statusOptions = ['Proposed', 'Approved', 'Rejected'], invoices, onSave, prefill = null, showFinance = true }) {
  const confirm = useConfirm();
  // Without Invoicing: no Total value, no payment terms — neither shown nor sent (see buildVariationPayload).
  const finance = financeVisible(showFinance, variation ? [variation] : []);
  const [draft, setDraft] = useState(() => variationToDraft(variation));
  // The VO's invoices, to warn before a term they point to is removed (Invoicing then refuses to edit them).
  // Passed in when the page has them; else read once per open (roles without Invoicing get none — no warning).
  const [loadedInvoices, setLoadedInvoices] = useState([]);
  useEffect(() => {
    if (!open || !finance || invoices !== undefined || !variation?.id || !variation?.project_id || !(variation.payment_terms || []).length) return undefined;
    let cancelled = false;
    api.get('/variation-invoices', { params: { project_id: variation.project_id } })
      .then((res) => { if (!cancelled) setLoadedInvoices(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (!cancelled) setLoadedInvoices([]); });
    return () => { cancelled = true; };
  }, [open, finance, invoices, variation?.id, variation?.project_id, variation?.payment_terms]);
  const voInvoices = useMemo(
    () => (invoices !== undefined ? invoices : loadedInvoices).filter((invoice) => !variation?.id || invoice.variation_id === variation.id),
    [invoices, loadedInvoices, variation?.id]
  );
  const [initial, setInitial] = useState(() => JSON.stringify(variationToDraft(variation)));
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteNote, setPasteNote] = useState('');

  useEffect(() => {
    if (!open) return;
    const start = variationToDraft(variation);
    if (!variation) start.vo_number = suggestNextVoNumber(variations);
    setInitial(JSON.stringify(start));
    if (!variation && prefill?.items?.length) {
      start.items = prefill.items.map(toDraftItem);
      if (prefill.description) start.description = prefill.description;
    }
    setDraft(start);
    setPasteOpen(false);
    setPasteText('');
    setPasteNote('');
    // A new panel per open; later changes of `variations` must not reset what is being typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, variation?.id]);

  const dirty = open && JSON.stringify(draft) !== initial;
  const boqIds = useMemo(() => new Set(boqItems.map((item) => item.id)), [boqItems]);
  const set = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }));
  const setItem = (key, field, value) => setDraft((prev) => ({ ...prev, items: prev.items.map((item) => (item.key === key ? { ...item, [field]: value } : item)) }));
  const removeItem = (key) => setDraft((prev) => ({ ...prev, items: prev.items.filter((item) => item.key !== key) }));
  const addItems = (items) => setDraft((prev) => {
    const next = [...prev.items];
    items.forEach((item) => {
      const existing = item.boq_item_id ? next.find((entry) => entry.boq_item_id === item.boq_item_id) : null;
      if (existing) existing.qty = String((parseQuantity(existing.qty) || 0) + (Number(item.qty) || 0));
      else next.push(toDraftItem(item));
    });
    return { ...prev, items: next.map((item) => ({ ...item })) };
  });
  const setTerm = (id, field, value) => setDraft((prev) => ({ ...prev, payment_terms: prev.payment_terms.map((term) => (term.id === id ? { ...term, [field]: value } : term)) }));
  const removeTerm = async (term) => {
    const linked = voInvoices.filter((invoice) => invoice.payment_term_id && invoice.payment_term_id === term.id);
    if (linked.length) {
      const ok = await confirm({
        title: `Remove “${String(term.name || '').trim() || 'this payment term'}”?`,
        description: `${linked.length === 1 ? '1 invoice is' : `${linked.length} invoices are`} linked to this term. After you save, they point to a term that no longer exists, and Invoicing asks for another term before they can be edited.`,
        consequences: linked.map((invoice) => `${invoice.reference || 'Invoice'} · ${invoice.invoice_type || 'Invoice'} · ${money(invoice.amount)}`),
        tone: 'danger',
        confirmLabel: 'Remove term',
      });
      if (!ok) return;
    }
    setDraft((prev) => ({ ...prev, payment_terms: prev.payment_terms.filter((entry) => entry.id !== term.id) }));
  };

  const valueCheck = typeof draft.total_value === 'string' && draft.total_value.trim() ? parseMoneyInput(draft.total_value) : { value: 0, error: '' };
  const totalValue = valueCheck.value || 0;
  const totalQty = draft.items.reduce((sum, item) => sum + (parseQuantity(item.qty) || 0), 0);
  const termsPercent = draft.payment_terms.reduce((sum, term) => sum + (Number(term.percentage) || 0), 0);

  const applyPaste = () => {
    const parsed = parseVariationPaste(pasteText, boqItems);
    if (!parsed.items.length) {
      setPasteNote('No rows with a quantity were found. Paste Part No, Description and Qty columns.');
      return;
    }
    addItems(parsed.items);
    setPasteText('');
    setPasteOpen(false);
    setPasteNote(`Added ${parsed.items.length} line${parsed.items.length === 1 ? '' : 's'} (${parsed.matched} matched to the BOQ${parsed.skipped ? `, ${parsed.skipped} skipped` : ''}).`);
  };

  const save = async () => {
    const { payload, error } = buildVariationPayload(draft, { includeFinance: finance });
    if (error) throw new Error(error);
    if (!variation) delete payload.status; // a new VO always starts as Proposed (server rule)
    await onSave(payload);
  };

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      width="lg"
      icon={GitCompareArrows}
      iconTone="violet"
      title={variation ? `Edit ${variation.vo_number || 'variation'}` : 'Add variation'}
      subtitle={variation ? (variation.description || 'Change order') : 'A change order on top of the base contract'}
      status={variation ? <StatusPill status={draft.status || 'Proposed'} domain="variation" size="sm" /> : null}
      dirty={dirty}
      onSave={save}
      saveLabel={variation ? 'Save variation' : 'Add variation'}
      testId="variation-panel"
    >
      <SidePanelSection title="Details">
        <div className={cn('grid gap-3', finance && 'sm:grid-cols-[160px_minmax(0,1fr)]')}>
          <label className="block space-y-1">
            <span className={FIELD_LABEL}>VO number</span>
            <Input value={draft.vo_number} onChange={(event) => set('vo_number', event.target.value)} aria-label="VO number" className={cn(fieldClass, MONO, !finance && 'sm:max-w-[160px]')} />
          </label>
          {finance ? (
          <label className="block space-y-1">
            <span className={FIELD_LABEL}>Total value (AED)</span>
            <Input
              value={draft.total_value}
              onChange={(event) => set('total_value', event.target.value)}
              inputMode="decimal"
              placeholder="0.00"
              aria-label="Total value"
              aria-invalid={valueCheck.error ? true : undefined}
              aria-describedby={valueCheck.error ? 'variation-total-value-error' : undefined}
              className={cn(fieldClass, 'text-right tabular-nums', valueCheck.error && 'border-status-red-ink')}
            />
            {valueCheck.error ? (
              <span id="variation-total-value-error" role="alert" className="block text-[12px] font-medium text-status-red-ink" data-testid="variation-value-error">
                {valueCheck.error}
              </span>
            ) : totalValue < 0 ? (
              <span className="block text-[12px] font-medium text-ink-3" data-testid="variation-omission-note">
                Omission — takes {money(-totalValue)} off the contract.
              </span>
            ) : null}
          </label>
          ) : null}
        </div>
        <label className="mt-3 block space-y-1">
          <span className={FIELD_LABEL}>Description</span>
          <Textarea value={draft.description} onChange={(event) => set('description', event.target.value)} aria-label="Description" placeholder="What the consultant asked for" className="min-h-[64px] rounded-lg bg-surface text-[13.5px]" />
        </label>
        {variation ? (
          <div className="mt-3 space-y-1">
            <span className={cn(FIELD_LABEL, 'block')}>Status</span>
            <Segmented
              ariaLabel="Variation status"
              value={draft.status}
              onChange={(next) => set('status', next)}
              options={statusOptions.map((option) => ({ key: option, label: option, dot: option === 'Approved' ? 'navy' : option === 'Rejected' ? 'red' : 'amber' }))}
            />
          </div>
        ) : (
          <p className="mt-2 text-[12.5px] text-ink-3">New variations start as <b className="font-semibold text-ink-2">Proposed</b>; set Approved when the consultant signs it.</p>
        )}
      </SidePanelSection>

      <SidePanelSection
        title={`Items${draft.items.length ? ` · ${draft.items.length} line${draft.items.length === 1 ? '' : 's'} · ${qtyText(totalQty)} pcs` : ''}`}
        description={`${boqItems.length ? 'Pick BOQ lines, paste from Excel or type items that are not in the BOQ yet.' : 'This project has no BOQ yet — type the items (or paste them); link them to the BOQ later.'} A negative quantity is an omission.`}
      >
        <div className="flex flex-wrap items-center gap-2">
          <BoqPicker boqItems={boqItems} onPick={(item) => addItems([{ boq_item_id: item.id, part_number: item.part_number, description: item.description, unit: item.unit, qty: 1 }])} />
          <Button type="button" variant="outline" size="sm" onClick={() => setPasteOpen(!pasteOpen)} aria-expanded={pasteOpen} data-testid="variation-paste-toggle">
            <ClipboardPaste aria-hidden="true" />
            Paste from Excel
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => addItems([{ qty: '' }])} data-testid="variation-add-line">
            <Plus aria-hidden="true" />
            Add line
          </Button>
          {shortages.length ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addItems(shortages.map((row) => ({ boq_item_id: row.id, part_number: row.part_number, description: row.description, unit: row.unit, qty: row.variationNeeded })))}
              title="Site needs more than BOQ + approved VOs for these lines"
              data-testid="variation-from-shortages"
            >
              <PackageSearch aria-hidden="true" />
              From Material shortages ({shortages.length})
            </Button>
          ) : null}
        </div>
        {pasteOpen ? (
          <div className="mt-3 space-y-2 rounded-lg border border-line bg-surface-2 p-3">
            <Textarea
              value={pasteText}
              onChange={(event) => setPasteText(event.target.value)}
              aria-label="Rows pasted from Excel"
              placeholder={'Part No ⇥ Description ⇥ Qty\nBE-GT20W.02\tGlass push button\t12'}
              className={cn('min-h-[96px] rounded-lg bg-surface text-[12.5px]', MONO)}
            />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setPasteOpen(false)}>Cancel</Button>
              <Button type="button" size="sm" variant="outline" onClick={applyPaste} disabled={!pasteText.trim()} data-testid="variation-paste-apply">Add pasted rows</Button>
            </div>
          </div>
        ) : null}
        {pasteNote ? <p className="mt-2 text-[12.5px] text-ink-3" role="status">{pasteNote}</p> : null}
        {draft.items.length ? (
          <div className="mt-3 overflow-hidden rounded-lg border border-line">
            <table className="w-full border-separate border-spacing-0 text-[13px]">
              <thead>
                <tr className="text-left text-[11.5px] font-bold text-ink-3">
                  <th className="border-b border-line bg-surface-2 px-2.5 py-1.5">Part no</th>
                  <th className="border-b border-line bg-surface-2 px-2.5 py-1.5">Description</th>
                  <th className="w-[84px] border-b border-line bg-surface-2 px-2.5 py-1.5 text-right">Qty</th>
                  <th className="w-[36px] border-b border-line bg-surface-2 px-1 py-1.5"><span className="sr-only">Remove</span></th>
                </tr>
              </thead>
              <tbody>
                {draft.items.map((item, index) => (
                  <tr key={item.key} data-testid={`variation-item-${index}`}>
                    <td className="border-b border-line-2 px-1.5 py-1">
                      <Input value={item.part_number} onChange={(event) => setItem(item.key, 'part_number', event.target.value)} aria-label={`Part number of item ${index + 1}`} className={cn('h-8 rounded-md border-transparent bg-transparent px-1.5 text-[12.5px] hover:border-line focus:border-line', MONO)} />
                      {item.boq_item_id && boqIds.has(item.boq_item_id) ? <span className="block px-1.5 text-[10.5px] font-semibold uppercase tracking-[0.04em] text-brand-ink">BOQ line</span> : null}
                    </td>
                    <td className="border-b border-line-2 px-1.5 py-1">
                      <Input value={item.description} onChange={(event) => setItem(item.key, 'description', event.target.value)} aria-label={`Description of item ${index + 1}`} className="h-8 rounded-md border-transparent bg-transparent px-1.5 text-[13px] hover:border-line focus:border-line" />
                    </td>
                    <td className="border-b border-line-2 px-1.5 py-1">
                      <Input value={item.qty} onChange={(event) => setItem(item.key, 'qty', event.target.value)} inputMode="decimal" aria-label={`Quantity of item ${index + 1}`} className="h-8 rounded-md text-right text-[13px] tabular-nums" />
                    </td>
                    <td className="border-b border-line-2 px-1 py-1 text-center">
                      <Button type="button" variant="ghost" size="icon-xs" onClick={() => removeItem(item.key)} aria-label={`Remove item ${index + 1}`}>
                        <X aria-hidden="true" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="mt-3 rounded-lg border border-dashed border-line px-3 py-3 text-[12.5px] text-ink-3">{finance ? 'No items yet. A VO can also be value only (no items).' : 'No items yet.'}</p>
        )}
      </SidePanelSection>

      {finance ? (
      <SidePanelSection
        title="Payment terms"
        description={totalValue ? `Of ${money(totalValue)}${termsPercent ? ` · ${pct(termsPercent)} planned` : ''}` : 'e.g. 50% on approval, 50% on delivery'}
      >
        <div className="space-y-2">
          {draft.payment_terms.map((term, index) => {
            const amount = term.target_amount ? 0 : (totalValue * (Number(term.percentage) || 0)) / 100;
            return (
              <div key={term.id} className="grid grid-cols-[minmax(0,1fr)_84px_120px_32px] items-center gap-2" data-testid={`variation-term-${index}`}>
                <Input value={term.name} onChange={(event) => setTerm(term.id, 'name', event.target.value)} placeholder="On approval" aria-label={`Name of payment term ${index + 1}`} className={fieldClass} />
                <div className="relative">
                  <Input value={term.percentage} onChange={(event) => setTerm(term.id, 'percentage', event.target.value)} inputMode="decimal" placeholder="50" aria-label={`Percentage of payment term ${index + 1}`} className={cn(fieldClass, 'pr-6 text-right tabular-nums')} />
                  <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[12px] text-ink-3">%</span>
                </div>
                <Input value={term.target_amount} onChange={(event) => setTerm(term.id, 'target_amount', event.target.value)} inputMode="decimal" placeholder={amount ? money(amount) : 'Amount'} aria-label={`Amount of payment term ${index + 1}`} className={cn(fieldClass, 'text-right tabular-nums')} />
                <Button type="button" variant="ghost" size="icon-xs" onClick={() => { void removeTerm(term); }} aria-label={`Remove payment term ${index + 1}`}>
                  <X aria-hidden="true" />
                </Button>
              </div>
            );
          })}
          <Button type="button" variant="ghost" size="sm" onClick={() => setDraft((prev) => ({ ...prev, payment_terms: [...prev.payment_terms, { id: newTermId(), name: '', percentage: '', target_amount: '' }] }))}>
            <Plus aria-hidden="true" />
            Add payment term
          </Button>
        </div>
      </SidePanelSection>
      ) : null}

      {variation?.created_at ? (
        <p className="px-1 text-[12px] text-ink-3">Created {formatDate(variation.created_at)}{variation.created_by ? ` by ${variation.created_by}` : ''}.</p>
      ) : null}
    </SidePanel>
  );
}

/** Material rows whose site needs exceed BOQ + approved VOs (what "From Material shortages" adds). */
export function useVariationShortages(materialRows = []) {
  return useMemo(() => materialRows.filter((row) => Number(row.variationNeeded || 0) > 0), [materialRows]);
}

